import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const ClientModal = ({
  open,
  onClose,
  onSave,
  clientData = null,
  mode = 'create',
  balanceSheetHeads = [],
  existingClients = []
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    BalanceSheetHeadId: 31,
    Name: '',
    AddressLine: '',
    ContactNumber: '',
    OpeningBalance: '',
    OpeningDate: new Date().toISOString().split('T')[0],
    IsPayableOrReceivable: false,
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (clientData && mode !== 'create') {
        fetchClientData(clientData.Id);
      } else {
        setFormData({
          BalanceSheetHeadId: 31,
          Name: '',
          AddressLine: '',
          ContactNumber: '',
          OpeningBalance: '',
          OpeningDate: new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: false,
        });
      }
      setErrors({});
    }
  }, [open, clientData, mode]);

  const fetchClientData = async (clientId) => {
    setLoading(true);
    try {
      const response = await fetchData('CustomerApi', `/Details/${clientId}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        const customer = response.response_data.Customer || response.response_data;
        setFormData({
          Id: customer.Id || '',
          BalanceSheetHeadId: customer.BalanceSheetHeadId || '',
          Name: customer.Name || '',
          AddressLine: customer.AddressLine || '',
          ContactNumber: customer.ContactNumber || '',
          OpeningBalance: customer.OpeningBalance || '',
          OpeningDate: customer.OpeningDate ? customer.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: customer.IsPayableOrReceivable || false,
          IsActive: customer.IsActive || false,
          CreatedBy: customer.CreatedBy || 0,
          CreatedDate: customer.CreatedDate ? customer.CreatedDate.split('T')[0] : new Date().toISOString().split('T')[0],
        });
      } else {
        alert('Error: Failed to load client data');
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      alert('Error: Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'ContactNumber' && value) {
      if (!/^[0-9+\-\s()]*$/.test(value)) return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name.trim()) {
      newErrors.Name = 'Client name is required';
    }

    if (!formData.BalanceSheetHeadId) {
      newErrors.BalanceSheetHeadId = 'Balance Sheet Head is required';
    }

    if (!formData.ContactNumber.trim()) {
      newErrors.ContactNumber = 'Contact number is required';
    } else {
      const digitsOnly = formData.ContactNumber.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 10) {
        newErrors.ContactNumber = 'Contact number must have at least 10 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateClient = () => {
    return existingClients.find(client => {
      if (mode === 'edit' && clientData && client.Id === clientData.Id) return false;
      const nameMatch = client.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
      const contactMatch = client.ContactNumber?.replace(/[^0-9]/g, '') === formData.ContactNumber.replace(/[^0-9]/g, '');
      return nameMatch && contactMatch;
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const duplicate = checkDuplicateClient();
    if (duplicate) {
      setDuplicateMessage(`A client with the name "${formData.Name}" and contact number "${formData.ContactNumber}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        BalanceSheetHeadId: parseInt(formData.BalanceSheetHeadId) || 0,
        Name: formData.Name,
        AddressLine: formData.AddressLine,
        ContactNumber: formData.ContactNumber,
        OpeningBalance: parseFloat(formData.OpeningBalance) || 0,
        OpeningDate: formData.OpeningDate,
        IsPayableOrReceivable: formData.IsPayableOrReceivable,
        CompanyId: companyId,
        Id: formData.Id || 0,
      };

      if (mode === 'edit') {
        if (!clientData || !clientData.Id) {
          alert('Error: Cannot update customer - missing ID');
          return;
        }
        dataToSend.Id = clientData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        await updateData('CustomerApi', clientData.Id, dataToSend, token);
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        await postData('CustomerApi', dataToSend, token);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Client';
      case 'edit': return 'Edit Client';
      case 'view': return 'Client Details';
      default: return 'Client';
    }
  };

  const isReadOnly = mode === 'view';
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '2px',
            margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '10px',
          overflow: 'hidden',
          height: isMobile ? '100vh' : 'auto'
        }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <PersonIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
                {getTitle()}
              </Typography>
            </Box>
            <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent
            className="client-modal-content"
            style={{
              padding: '8px 12px',
              paddingBottom: '4px',
              maxHeight: isMobile ? 'calc(100vh - 120px)' : '70vh',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            <style>{`
              .client-modal-content::-webkit-scrollbar { width: 0px; display: none; }
              .client-modal-content { scrollbar-width: none; -ms-overflow-style: none; }
            `}</style>

            <Typography variant="subtitle2" style={{ color: '#666', marginBottom: '6px', fontSize: '10px', lineHeight: '1.2' }}>
              {mode === 'create' ? 'Add a new client to your system' :
                mode === 'edit' ? 'Update client information' : 'View client details'}
            </Typography>

            <Grid container spacing={1}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px' }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="Name"
                  label="Client Name *"
                  value={formData.Name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.Name}
                  helperText={errors.Name}
                  disabled={isReadOnly}
                  placeholder="Enter client name"
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  select
                  name="BalanceSheetHeadId"
                  label="Balance Sheet Head *"
                  value={formData.BalanceSheetHeadId}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.BalanceSheetHeadId}
                  helperText={errors.BalanceSheetHeadId}
                  disabled={isReadOnly}
                  SelectProps={{ native: true }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                >
                  <option value=""></option>
                  {balanceSheetHeads.map(head => (
                    <option key={head.Id} value={head.Id}>{head.Name}</option>
                  ))}
                </TextField>
              </Grid>

              {/* Address Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', marginTop: '2px', color: '#2d3748', fontSize: '12px' }}>
                  Address Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="AddressLine"
                  label="Address"
                  value={formData.AddressLine}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter address"
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              {/* Contact Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', marginTop: '2px', color: '#2d3748', fontSize: '12px' }}>
                  Contact Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="ContactNumber"
                  label="Contact Number *"
                  value={formData.ContactNumber}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.ContactNumber}
                  helperText={errors.ContactNumber}
                  disabled={isReadOnly}
                  placeholder="+91 XXXXX XXXXX"
                  inputProps={{ maxLength: 15 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Financial & Other Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', marginTop: '2px', color: '#2d3748', fontSize: '12px' }}>
                  Financial & Other Details
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  name="OpeningBalance"
                  label="Opening Balance"
                  type="number"
                  value={formData.OpeningBalance}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  name="OpeningDate"
                  label="Opening Date"
                  type="date"
                  value={formData.OpeningDate}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  InputLabelProps={{ shrink: true, style: labelStyle }}
                  InputProps={{ style: inputStyle }}
                />
              </Grid>

              <Grid item xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                <Box style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                  minHeight: '32px',
                  width: '100%'
                }}>
                  <input
                    type="checkbox"
                    name="IsPayableOrReceivable"
                    checked={formData.IsPayableOrReceivable}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    style={{ width: '12px', height: '12px', accentColor: '#667eea' }}
                  />
                  <Typography style={{ fontSize: '11px', fontWeight: '500', color: '#4a5568' }}>
                    Is Payable or Receivable
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions style={{
            padding: '8px 12px',
            background: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            gap: '6px',
            minHeight: 'auto'
          }}>
            <Button
              onClick={onClose}
              style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                disabled={loading}
                variant="contained"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '600',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  fontSize: '12px'
                }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Add Client' : 'Update Client'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px' }}>
            Duplicate Client Found
          </Typography>
          <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
            {duplicateMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: '600',
              borderRadius: '8px',
              padding: '8px 32px',
              fontSize: '14px'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientModal;
